/**
 * V2 Place page — one page per location, everyone's yoinks on it.
 * Art reference: docs/concepts/atlas-mockup.png (right panel).
 *
 * Phase 2 scope: map-texture header, stat stamps (yoinks / explorers),
 * the log list. Friends strip arrives with follows (Phase 3); the
 * "permanently closed" stamp arrives with the locations.status column;
 * live map arrives once the Maps key allows the subdomain.
 */
import * as gateway from '../../core/gateway.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { thumbUrl } from '../../ui/card.js';
import { handleShareClick } from '../../ui/share.js';
import { createSharePhotosHandler } from '../../ui/share-photos.js';
import { hashtags } from '../../ui/share-card.js';
import { getMetadata, loadAlsoVisited, reportPlaceGone } from './metadata.js';
import { showToast } from '../../ui/toast.js';
import { loadSaved } from '../lists/lists.js';
import { handleSaveClick } from '../lists/save-button.js';

// Split 2026-07-25: place.js was 488 lines against the plan's ~400 budget,
// but unusually easy to divide — renderPlace already took all seven of its
// inputs as parameters, so the whole render layer lifted out untouched.
// Reads are in ./place-data.js, rendering in ./place-view.js. The four
// exported derivations are re-exported below UNCHANGED for the tests.
import { fetchLocation, fetchLogs } from './place-data.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';
import {
  renderPlace, computeStats, deriveFriends, heroPhotos, deriveLogCategories,
} from './place-view.js';

export { computeStats, deriveFriends, heroPhotos, deriveLogCategories };


/**
 * Mount the place view.
 * @param {HTMLElement} el
 * @param {object} ctx - { store, router, params: { id } }
 */
export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let destroyed = false;
  let catFilter = null; // V1's in-place category pills
  let loaded = null; // { location, logs } once fetched

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';

  Promise.all([fetchLocation(params.id), fetchLogs(params.id)])
    .then(async ([location, logs]) => {
      if (destroyed) return;
      if (!location) {
        el.innerHTML = '<div class="v2-empty">This place isn’t on the map.</div>';
        return;
      }
      loaded = { location, logs, metadata: location.metadata || {}, coVisited: [] };
      document.title = `${location.display_name} | Yoink Adventures v2`;
      setRibbonContext(`Been to ${location.display_name}? Log it with a photo — that’s a yoink.`);
      renderPlace(el, location, logs, store, loaded.metadata, loaded.coVisited, catFilter);
      // Rich details + co-visited settle after first paint (V1 pattern)
      getMetadata(location).then((metadata) => {
        if (destroyed) return;
        loaded.metadata = metadata;
        renderPlace(el, location, logs, store, loaded.metadata, loaded.coVisited, catFilter);
      }).catch(() => {});
      loadAlsoVisited(location.id, logs).then((coVisited) => {
        if (destroyed) return;
        loaded.coVisited = coVisited;
        renderPlace(el, location, logs, store, loaded.metadata, loaded.coVisited, catFilter);
      }).catch(() => {});
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  // The friends strip and save button depend on auth-following store
  // slices, which can settle after the page renders (login, follows and
  // saved places finishing their loads).
  if (store.get().user) loadSaved(store);
  const unsubscribe = store.subscribe(() => {
    if (destroyed) return;
    if (store.get().user) loadSaved(store);
    if (loaded) renderPlace(el, loaded.location, loaded.logs, store, loaded.metadata, loaded.coVisited, catFilter);
  });

  // The place as a card set: its name over the photos every explorer
  // logged here — all from the already-loaded logs, no extra queries.
  const sharePhotos = createSharePhotosHandler(el, () => {
    if (!loaded) return null;
    const { location, logs } = loaded;
    // The same thumbUrl the page's photo strip displays — cached, so
    // the cards build inside the tap's activation window
    const photos = logs
      .map((l) => (Array.isArray(l.images) ? l.images.find(Boolean) : null))
      .filter(Boolean).slice(0, 10)
      .map((img) => thumbUrl(img.startsWith('http') ? img : gateway.getPublicUrl('yoinks', img)));
    const address = location.formatted_address || loaded.metadata?.formatted_address || '';
    return {
      baseName: `yoink-${slugify(location.display_name || '') || 'place'}`,
      title: location.display_name || 'A place',
      // First two address segments only — a full address overflows the card
      metaLine: address.split(',').slice(0, 2).join(',').trim(),
      byline: '',
      stampDate: null,
      category: logs[0]?.categories || null,
      photos,
      hashtags: hashtags({ categories: logs[0]?.categories }),
    };
  });

  function activate(event) {
    if (handleShareClick(event)) return;
    if (sharePhotos(event)) return;
    if (handleSaveClick(event, { store, locationId: params.id, el })) return;
    const reportBtn = event.target.closest('[data-report-gone]');
    if (reportBtn && loaded) {
      // Feedback stays ON the page (iPad — no console): the button turns
      // into its own progress line, then a toast + re-render say the verdict.
      reportBtn.disabled = true;
      reportBtn.textContent = 'Checking with Google…';
      reportPlaceGone(loaded.location).then((outcome) => {
        if (destroyed || !loaded) return;
        if (outcome === 'confirmed_permanent' || outcome === 'confirmed_temporary') {
          loaded.location.status =
            outcome === 'confirmed_permanent' ? 'closed_permanently' : 'closed_temporarily';
          // The banner reads metadata.business_status FIRST — a stale
          // "OPERATIONAL" there would mask the stamp we just confirmed
          loaded.metadata = {
            ...loaded.metadata,
            business_status: outcome === 'confirmed_permanent' ? 'CLOSED_PERMANENTLY' : 'CLOSED_TEMPORARILY',
          };
          showToast('Google confirms it — the closed stamp is up. Thanks for the heads-up.',
            { type: 'success' });
        } else if (outcome === 'reported') {
          // Local echo only — the RPC stamped the real (server) timestamp
          loaded.metadata = { ...loaded.metadata, reported_closed_at: new Date().toISOString() };
          showToast('Thanks — Google still lists it as open, so we’ve flagged it for a human to check.',
            { type: 'success' });
        } else {
          showToast('Couldn’t send the report — try again in a bit.', { type: 'info' });
        }
        renderPlace(el, loaded.location, loaded.logs, store, loaded.metadata, loaded.coVisited, catFilter);
      });
      return;
    }
    const logHere = event.target.closest('[data-log-here]');
    if (logHere) {
      event.preventDefault();
      router.navigate('log', { locationId: params.id });
      return;
    }
    const covisit = event.target.closest('[data-covisit-id]');
    if (covisit) {
      router.navigate('location', { id: covisit.dataset.covisitId });
      return;
    }
    const friend = event.target.closest('[data-profile-username]');
    if (friend) {
      router.navigate('user', { username: friend.dataset.profileUsername });
      return;
    }
    const headCat = event.target.closest('[data-head-cat]');
    if (headCat) {
      router.navigate('category', { slug: headCat.dataset.headCat });
      return;
    }
    const pill = event.target.closest('[data-place-pill]');
    if (pill) {
      const id = pill.dataset.placePill || null;
      if (id && id === catFilter) {
        // V1: tapping the already-active pill opens the category page
        router.navigate('category', { slug: pill.dataset.pillSlug });
        return;
      }
      catFilter = id;
      if (loaded) renderPlace(el, loaded.location, loaded.logs, store, loaded.metadata, loaded.coVisited, catFilter);
      return;
    }
    const logUser = event.target.closest('[data-log-user]');
    if (logUser) {
      router.navigate('user', { username: logUser.dataset.logUser });
      return;
    }
    const entry = event.target.closest('[data-yoink-id]');
    if (entry) router.navigate('yoink', { id: entry.dataset.yoinkId });
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest('[data-yoink-id], [data-log-user]')) return;
    event.preventDefault();
    activate(event);
  }

  el.addEventListener('click', activate);
  el.addEventListener('keydown', onKeydown);

  return () => {
    destroyed = true;
    unsubscribe();
    el.removeEventListener('click', activate);
    el.removeEventListener('keydown', onKeydown);
  };
}
